import type { HTMLAttributes } from 'react';

import { cn } from '../lib/cn';
import { StatusBadge, type StatusBadgeStatus } from './status-badge';

/** Zelfde vier waarden als de funnel — één dekkingsbegrip door de hele module heen. */
export type StatusStripCoverageState = 'covered' | 'no_data' | 'publisher_offline' | 'unmapped';

export interface StatusStripStep {
  /** Stapnaam, al vertaald door de aanroeper — deze component is taal-neutraal. */
  label: string;
  /** Producttelling; `null` wanneer de databron deze stap niet kent (nooit een 0 die als leeg leest). */
  count: number | null;
  coverageState: StatusStripCoverageState;
  /** Tekst van de badge onder de tegel, al vertaald (bv. "in behandeling" / "niet bijgehouden"). */
  statusLabel: string;
  /**
   * Wat er in plaats van een telling staat bij déze stap. Nodig omdat de reden per stap verschilt:
   * "nog niet bijgehouden" is iets anders dan "bron niet bereikbaar". Valt terug op `noDataLabel`.
   */
  noDataLabel?: string;
  /** Stabiele sleutel voor `data-step` — selectors hoeven zo niet op taal te leunen. */
  stepKey?: string;
}

export interface StatusStripProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  ariaLabel: string;
  steps: StatusStripStep[];
  /** Standaardtekst in plaats van een telling, voor stappen zonder eigen `noDataLabel`. */
  noDataLabel: string;
  /** Formatteert de zichtbare telling (bv. "{{count}} producten"); standaard het kale getal. */
  formatCount?: (count: number) => string;
}

/**
 * UXR/FR4 — de status-strip: ketenstappen als tegels **naast elkaar**, nadrukkelijk geen versmallende
 * funnel. Die vorm is hier verboden (DESIGN.md): de tellingen zijn niet gegarandeerd monotoon, dus een
 * funnel zou een doorstroom suggereren die er niet is.
 *
 * Een stap zonder databron krijgt de gestreepte, gedempte staat — nooit de amber/rode taal van een
 * knelpunt (SM-C1: "we zien niets" mag niet lezen als "er gebeurt niets"). Elke tegel draagt icoon,
 * label én kleur, plus een eigen `aria-label`, zodat de staat ook zonder kleurwaarneming leesbaar is.
 *
 * Op brede schermen blijft de keten één rij met eigen horizontale overloop: de volgorde ís de
 * boodschap, dus de ladder mag niet halverwege afbreken zodra er stappen bijkomen.
 */
export function StatusStrip({ ariaLabel, className, formatCount, noDataLabel, steps, ...props }: StatusStripProps) {
  return (
    <div {...props} aria-label={ariaLabel} className={cn('grid grid-cols-2 gap-3 md:grid-cols-3 lg:auto-cols-[minmax(9rem,1fr)] lg:grid-flow-col lg:overflow-x-auto', className)} data-testid="status-strip" role="list">
      {steps.map((step, index) => {
        // Een negatieve telling is geen geldige stand: die tonen zou een getal en een status opleveren
        // die elkaar tegenspreken. Behandel hem als "geen telling", net als null.
        const isCovered = step.coverageState === 'covered' && step.count != null && step.count >= 0;
        const emptyLabel = step.noDataLabel ?? noDataLabel;
        const countLabel = step.count != null && formatCount ? formatCount(step.count) : String(step.count);
        const badgeStatus: StatusBadgeStatus = !isCovered ? 'process-no-data' : step.count! > 0 ? 'process-current' : 'process-future';

        return (
          <div
            aria-label={`${step.label}: ${isCovered ? countLabel : emptyLabel} — ${step.statusLabel}`}
            className={cn('flex flex-col gap-2 rounded-[var(--radius-card)] border p-3', isCovered ? 'border-[var(--border)] bg-[var(--background)]' : 'border-dashed border-[var(--border)] bg-[var(--background-muted)]')}
            data-coverage-state={step.coverageState}
            data-step={step.stepKey ?? step.label}
            data-testid="status-strip-step"
            key={`${step.stepKey ?? step.label}-${index}`}
            role="listitem"
          >
            <p className="text-[11px] font-medium uppercase tracking-wide text-[var(--foreground-muted)]">{step.label}</p>
            {isCovered ? (
              <p className="text-2xl font-bold leading-none text-[var(--foreground)]" data-testid="status-strip-count">{countLabel}</p>
            ) : (
              <p className="text-xs text-[var(--foreground-muted)]" data-testid="status-strip-no-data">{emptyLabel}</p>
            )}
            {/* announce=false: in een lijst van zes tot acht tegels zou elke badge een eigen live region
                zijn en bij elke ververs meepraten. De tegel draagt de betekenis al in zijn aria-label. */}
            <StatusBadge announce={false} label={step.statusLabel} status={badgeStatus} />
          </div>
        );
      })}
    </div>
  );
}
